import { queryOptions, useQuery } from '@tanstack/react-query';
import { ActivityService } from '../../generated';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { EdgeSizeChipData } from '../../../components/Prediction/types';
import { STALE_TIME_LIVE_VIEW } from '../../queryClient';
import { transformEdgeEstimation } from '../edgeEstimation';

export const activityEdgeEstimationKeys = {
  base: () => ['activityEdgeEstimation'] as const,
  one: ({ jobsiteId, captureStartAt }: ActivityEdgeEstimationOptionProps) =>
    [...activityEdgeEstimationKeys.base(), jobsiteId, captureStartAt] as const,
};

export interface ActivityEdgeEstimationOptionProps {
  jobsiteId: string;
  captureStartAt: string;
}

export function activityEdgeEstimationOptions({
  jobsiteId,
  captureStartAt,
  enabled,
}: ActivityEdgeEstimationOptionProps & { enabled: boolean }) {
  return queryOptions<EdgeSizeChipData[], ApiError>({
    queryKey: activityEdgeEstimationKeys.one({ jobsiteId, captureStartAt }),
    queryFn: async () => {
      if (!jobsiteId || !captureStartAt) {
        throw new Error('jobsiteId and captureStartAt are required');
      }

      const response = await ActivityService.activityControllerGetEdgeEstimation({
        path: { jobsiteId, capture_start_at: captureStartAt },
      });

      if (response.data !== undefined) {
        return transformEdgeEstimation(response.data.output);
      } else {
        throw (response as any).error || new Error(response?.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!captureStartAt && enabled,
    staleTime: STALE_TIME_LIVE_VIEW,
  });
}

export function useActivityEdgeEstimation(props: ActivityEdgeEstimationOptionProps & { enabled: boolean }) {
  return useQuery(activityEdgeEstimationOptions(props));
}
